import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Gavel, Plus, Loader2 } from "lucide-react";

export const Disputes = () => {
  const qc = useQueryClient();
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ subject_type: "parcel", subject_ref: "", complainant: "", nature: "boundary", narrative: "" });

  const { data } = useQuery({ queryKey: ["disputes"], queryFn: async () => (await api.get("/disputes")).data });
  const create = useMutation({
    mutationFn: async () => (await api.post("/disputes", form)).data,
    onSuccess: () => { toast.success("Dispute filed"); qc.invalidateQueries({ queryKey: ["disputes"] }); setShow(false); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const items = data || [];

  return (
    <div className="space-y-6" data-testid="page-disputes">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">Dispute360™</div>
          <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1">Objections & Disputes</h1>
          <div className="text-[13px] text-[#B3C2CC] mt-1">Boundary, overlap, ownership and administrative reviews.</div>
        </div>
        <button onClick={() => setShow(!show)} data-testid="btn-new-dispute" className="cad-btn-primary px-4 py-2 rounded-sm inline-flex items-center gap-2 text-sm">
          <Plus size={16} /> File dispute
        </button>
      </header>

      {show && (
        <div className="cad-panel p-5" data-testid="dispute-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <select value={form.subject_type} onChange={(e) => setForm({ ...form, subject_type: e.target.value })}
                    className="bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]">
              <option value="parcel">Parcel</option>
              <option value="mineral_right">Mineral Right</option>
            </select>
            <input value={form.subject_ref} onChange={(e) => setForm({ ...form, subject_ref: e.target.value })} placeholder="Reference"
                   className="bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]" />
            <input value={form.complainant} onChange={(e) => setForm({ ...form, complainant: e.target.value })} placeholder="Complainant"
                   className="bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]" />
            <select value={form.nature} onChange={(e) => setForm({ ...form, nature: e.target.value })}
                    className="bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]">
              {["boundary","ownership","overlap","unlawful_occupation","administrative_review"].map((x) => <option key={x}>{x}</option>)}
            </select>
            <textarea value={form.narrative} onChange={(e) => setForm({ ...form, narrative: e.target.value })} placeholder="Narrative"
                      className="md:col-span-2 bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]" rows={3} />
          </div>
          <button onClick={() => create.mutate()} disabled={create.isPending} data-testid="dispute-submit"
                  className="cad-btn-primary mt-3 px-4 py-2 rounded-sm text-sm inline-flex items-center gap-2">
            {create.isPending && <Loader2 size={14} className="animate-spin"/>} Submit dispute
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((d) => (
          <div key={d.id} className="cad-panel p-4" data-testid={`dispute-${d.id}`}>
            <div className="flex items-center justify-between">
              <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E] flex items-center gap-1.5">
                <Gavel size={12}/> {d.nature.replace(/_/g, " ")}
              </div>
              <span className={`cad-chip ${d.status === "closed" ? "cad-chip-green" : d.status === "under_review" ? "cad-chip-blue" : "cad-chip-gold"}`}>{d.status.replace(/_/g, " ")}</span>
            </div>
            <div className="font-display font-bold text-[#F6F8FA] mt-1">{d.subject_ref}</div>
            <div className="text-[12px] text-[#B3C2CC] mt-1">Complainant: <span className="text-[#F6F8FA]">{d.complainant}</span></div>
            <div className="text-[12px] text-[#B3C2CC] mt-2">{d.narrative}</div>
          </div>
        ))}
      </div>
    </div>
  );
};
